import { MouseEvent, useState } from 'react';
import { ListItem, ListItemPending } from '../../AssetListItems';
import { useUploadAssetsContext } from '../../../contexts/UploadAssetsContext';
import { useTakeoverContext } from '../../../contexts/Takeover';
import { TakeoverIntent } from '../../../types';
import { useDeleteImageOverlayContext } from 'modules/creativeLibrary/contexts/DeleteImageOverlayContext';
import { useAssetsQueryContext } from 'modules/creativeLibrary/contexts/AssetsQueryContext';

// displays a list of pending uploads (ready to upload, uploading, successful upload, or error)
export const AssetListPending = () => {
  const { setImgsIdsToDelete } = useDeleteImageOverlayContext();
  const { pendingImages, setSelectedAssets } = useUploadAssetsContext();
  const { currentFolder } = useAssetsQueryContext();
  const [lastSelected, setLastSelected] = useState<string>('');
  const { currentIntent } = useTakeoverContext();
  const isEditMode = currentIntent === TakeoverIntent.EDIT;
  const currenFolderName =
    currentFolder?.name === '__root__' ? undefined : currentFolder?.name || '';
  const handleSelectAsset = (e: MouseEvent, id: string) => {
    const { ctrlKey, metaKey, shiftKey } = e;
    const idIsSelected = pendingImages.find((pi) => pi.id === id)?.isSelected || false;
    const selectedIds = pendingImages.filter((pi) => pi.isSelected).map((pi) => pi.id);
    // using a set makes add/delete operations simple & guarantees no duplicates
    const selectedIdSet = new Set(selectedIds);
    const getSelectedIds = () => {
      if (!ctrlKey && !metaKey && !shiftKey) {
        // just toggle the current item & clear all others
        if (idIsSelected) {
          setLastSelected('');
          return [];
        } else {
          setLastSelected(id);
          return [id];
        }
      } else if (ctrlKey || metaKey) {
        // keep existing selections, but toggle current item
        // windows: ctrl key + click; mac: cmd key (aka 'metaKey') + click
        if (idIsSelected) {
          setLastSelected('');
          selectedIdSet.delete(id);
        } else {
          setLastSelected(id);
          selectedIdSet.add(id);
        }
        return Array.from(selectedIdSet);
      } else {
        // shiftKey (only remaining option)
        // select a range (if a previous selection exists) - always select, never deselect
        const lastSelectedIndex = pendingImages.findIndex((pi) => pi.id === lastSelected);
        const thisSelectedIndex = pendingImages.findIndex((pi) => pi.id === id);
        const markers = [lastSelectedIndex, thisSelectedIndex].sort(); // order is important for slice
        setLastSelected(id);
        if (lastSelectedIndex === -1 || lastSelectedIndex === thisSelectedIndex) {
          selectedIdSet.add(id);
          return Array.from(selectedIdSet);
        } else {
          pendingImages
            .slice(markers[0], markers[1] + 1)
            .map((pi) => pi.id)
            .forEach((sid) => selectedIdSet.add(sid));
          return Array.from(selectedIdSet);
        }
      }
    };

    setSelectedAssets(getSelectedIds());
  };

  // completed uploads will have the .asset key defined (the api return of a successful upload)

  return !pendingImages.length ? null : (
    <>
      {pendingImages.map((pendingAsset) =>
        pendingAsset.asset ? (
          <ListItem
            asset={pendingAsset.asset}
            isSelected={isEditMode ? pendingAsset.isSelected : false}
            key={`asset_${pendingAsset.asset.id}`}
            onClick={isEditMode ? (e) => handleSelectAsset(e, pendingAsset.id) : undefined}
            destinationFolderName={pendingAsset.selected.destinationFolderName || currenFolderName}
            onDelete={() => setImgsIdsToDelete([pendingAsset.id])}
          />
        ) : (
          <ListItemPending
            image={pendingAsset}
            isSelected={pendingAsset.isSelected}
            key={`pending_${pendingAsset.id}`}
            onClick={(e) => handleSelectAsset(e, pendingAsset.id)}
            destinationFolderName={pendingAsset.selected.destinationFolderName || currenFolderName}
            onDelete={() => setImgsIdsToDelete([pendingAsset.id])}
          />
        ),
      )}
    </>
  );
};
